import React, { useState, useEffect } from 'react';
import AdminBackLink from '../components/AdminBackLink';

import axiosInstance from '../services/api';
import api from '../config/api';
import { Announcement } from '../types';
import { HiOutlinePencil, HiOutlineTrash, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

const Announcements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);

  const [formData, setFormData] = useState({
    contentAr: '',
    contentEn: '',
    isActive: true,
    orderIndex: 0,
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(api.announcements.getAll());
      setAnnouncements(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحميل الإعلانات');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      contentAr: announcement.contentAr,
      contentEn: announcement.contentEn || '',
      isActive: announcement.isActive,
      orderIndex: announcement.orderIndex,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الخبر؟')) return;

    try {
      setError('');
      setSuccess('');
      await axiosInstance.delete(api.announcements.delete(id));
      setSuccess('تم حذف الخبر بنجاح');
      fetchAnnouncements();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل حذف الخبر');
    }
  };

  const handleToggleActive = async (announcement: Announcement) => {
    try {
      setError('');
      setSuccess('');
      await axiosInstance.put(api.announcements.update(announcement.id), {
        ...announcement,
        isActive: !announcement.isActive,
      });
      setSuccess('تم تحديث حالة الخبر بنجاح');
      fetchAnnouncements();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحديث حالة الخبر');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      if (editingAnnouncement) {
        await axiosInstance.put(api.announcements.update(editingAnnouncement.id), formData);
        setSuccess('تم تحديث الخبر بنجاح');
      } else {
        await axiosInstance.post(api.announcements.create, formData);
        setSuccess('تم إضافة الخبر بنجاح');
      }

      setShowForm(false);
      setEditingAnnouncement(null);
      setFormData({
        contentAr: '',
        contentEn: '',
        isActive: true,
        orderIndex: 0,
      });
      fetchAnnouncements();
    } catch (err: any) {
      setError(err.response?.data?.error || `فشل ${editingAnnouncement ? 'تحديث' : 'إضافة'} الخبر`);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAnnouncement(null);
    setFormData({
      contentAr: '',
      contentEn: '',
      isActive: true,
      orderIndex: 0,
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-paper)' }}>
      <nav
        style={{
          backgroundColor: '#041525',
          borderBottom: '2px solid #477CD7',
          width: '100%',
          height: '72px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full relative">
            <AdminBackLink />
            <h1 
              className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2"
              style={{
                fontFamily: 'var(--font-arabtype)',
                letterSpacing: '0.5px',
              }}
            >
              شريط الأخبار
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </nav>

      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
              {success}
            </div>
          )}

          {/* Header */}
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold" style={{ color: '#ffffff' }}>قائمة الأخبار</h2>
            <button
              onClick={() => {
                handleCancel();
                setShowForm(true);
              }}
              className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
              style={{
                background: 'var(--button-gradient)',
              }}
            >
              إضافة خبر جديد
            </button>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {editingAnnouncement ? 'تعديل الخبر' : 'إضافة خبر جديد'}
                    </h2>
                    <button
                      onClick={handleCancel}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          المحتوى بالعربية *
                        </label>
                        <textarea
                          value={formData.contentAr}
                          onChange={(e) => setFormData({ ...formData, contentAr: e.target.value })}
                          required
                          rows={4}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                          placeholder="أدخل نص الخبر بالعربية"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          المحتوى بالإنجليزية
                        </label>
                        <textarea
                          value={formData.contentEn}
                          onChange={(e) => setFormData({ ...formData, contentEn: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                          placeholder="أدخل نص الخبر بالإنجليزية (اختياري)"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ترتيب العرض
                        </label>
                        <input
                          type="number"
                          value={formData.orderIndex}
                          onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 0 })}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                          style={{
                            borderColor: 'var(--border-main)',
                          } as React.CSSProperties}
                        />
                      </div>

                      <div className="flex items-center mt-8">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="mr-2 text-sm font-medium text-gray-700">
                            نشط
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="flex gap-4 justify-end pt-4">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                      >
                        إلغاء
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
                        style={{
                          background: 'var(--button-gradient)',
                        }}
                      >
                        {editingAnnouncement ? 'تحديث' : 'إضافة'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Cards */}
          {loading ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>جاري التحميل...</div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>لا توجد أخبار</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {announcements.map((announcement) => (
                <div
                  key={announcement.id}
                  className="p-6 rounded-lg transition-all"
                  style={{
                    background: 'rgba(8, 42, 73, 0.6)',
                    border: '1px solid transparent',
                    color: '#ffffff',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="mb-2">
                        <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>المحتوى (عربي)</span>
                        <p className="text-sm font-medium mt-1 line-clamp-2">{announcement.contentAr}</p>
                      </div>
                      {announcement.contentEn && (
                        <div className="mb-2">
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>المحتوى (إنجليزي)</span>
                          <p className="text-sm mt-1 line-clamp-2" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{announcement.contentEn}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>الترتيب:</span>
                        <span className="text-sm font-medium">{announcement.orderIndex}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>التاريخ:</span>
                        <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{formatDate(announcement.createdAt)}</span>
                      </div>
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${
                          announcement.isActive
                            ? 'bg-green-500 bg-opacity-20 text-green-300'
                            : 'bg-gray-500 bg-opacity-20 text-gray-300'
                        }`}
                      >
                        {announcement.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <button
                        onClick={() => handleEdit(announcement)}
                        className="p-2 rounded-lg transition-all hover:bg-blue-500 hover:bg-opacity-20"
                        style={{ color: '#477CD7' }}
                        title="تعديل"
                      >
                        {React.createElement(HiOutlinePencil as any, { size: 18 })}
                      </button>
                      <button
                        onClick={() => handleToggleActive(announcement)}
                        className="p-2 rounded-lg transition-all hover:bg-yellow-500 hover:bg-opacity-20"
                        style={{ color: announcement.isActive ? '#F59E0B' : '#10B981' }}
                        title={announcement.isActive ? 'تعطيل' : 'تفعيل'}
                      >
                        {announcement.isActive 
                          ? React.createElement(HiOutlineEyeOff as any, { size: 18 })
                          : React.createElement(HiOutlineEye as any, { size: 18 })
                        }
                      </button>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="p-2 rounded-lg transition-all hover:bg-red-500 hover:bg-opacity-20"
                        style={{ color: '#EF4444' }}
                        title="حذف"
                      >
                        {React.createElement(HiOutlineTrash as any, { size: 18 })}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Announcements;

